import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { alpha, Box, Button, Stack, Typography, useTheme } from "@mui/material";
import StyledLink from "../../../component/StyledLink";
import useIsCurPage from "../../../hook/useIsCurPage";
import { courseCatalogCategories, getCourseCatalogCategoryPath } from "../../../page/CourseDetailsPage/const/courseCatalog";

export type CourseNavigationMenuProps = Readonly<{
  textColor: string;
  variant?: "desktop" | "mobile";
}>;

export default function CourseNavigationMenu({ textColor, variant = "desktop" }: CourseNavigationMenuProps) {
  const theme = useTheme();
  const isCoursePage = useIsCurPage({ dir: "courseDetails" });
  const isDesktop = variant === "desktop";

  if (!isDesktop) {
    return (
      <Stack>
        <Typography variant="subtitle1" color={isCoursePage ? "primary.light" : textColor} sx={{ p: 2 }}>
          課程詳情
        </Typography>
        <CourseCategoryLinks layout="mobile" />
      </Stack>
    );
  }

  return (
    <Box
      sx={{
        height: 1,
        position: "relative",
        "& .course-navigation-panel": {
          display: "none",
          opacity: 0,
          transition: theme.transitions.create("opacity", { duration: theme.transitions.duration.shortest }),
        },
        "&:hover": {
          "& .course-navigation-trigger": {
            color: "primary.light",
          },
          "& .course-navigation-panel": {
            display: "grid",
            opacity: 1,
          },
        },
      }}
    >
      <Button disableRipple sx={{ height: 1, p: 0, "&:hover": { bgcolor: "transparent" } }}>
        <Typography
          className="course-navigation-trigger"
          variant="subtitle1"
          color={isCoursePage ? "primary.light" : textColor}
          sx={{ px: 1 }}
        >
          課程詳情
        </Typography>
      </Button>

      <Box
        className="course-navigation-panel"
        component="nav"
        sx={{
          position: "absolute",
          top: "100%",
          right: "50%",
          transform: "translate(50%, 0)",
          width: 330,
          gap: 1,
          p: 2,
          border: `1px solid ${alpha(theme.palette.primary.contrastText, 0.12)}`,
          bgcolor: alpha(theme.palette.primary.main, 0.96),
          boxShadow: 8,
        }}
      >
        <CourseCategoryLinks layout="desktop" />
      </Box>
    </Box>
  );
}

function CourseCategoryLinks({ layout }: Readonly<{ layout: "desktop" | "mobile" }>) {
  const theme = useTheme();

  return (
    <Stack spacing={layout === "desktop" ? 1 : 0}>
      {courseCatalogCategories.map((category) => (
        <Button
          key={category.id}
          component={StyledLink}
          to={getCourseCatalogCategoryPath(category.id)}
          sx={{
            justifyContent: "space-between",
            textAlign: "left",
            borderRadius: 0,
            px: layout === "desktop" ? 2 : 4,
            py: 1.5,
            color: "primary.contrastText",
            bgcolor: layout === "desktop" ? alpha(theme.palette.primary.contrastText, 0.035) : "transparent",
            "&:hover": {
              bgcolor: alpha(theme.palette.info.main, 0.12),
            },
          }}
        >
          <Stack spacing={0.5} alignItems="flex-start">
            <Typography variant="body1">{category.label}</Typography>
            <Typography variant="caption" sx={{ color: alpha(theme.palette.primary.contrastText, 0.62) }}>
              {category.description}
            </Typography>
          </Stack>
          <ArrowForwardRoundedIcon fontSize="small" />
        </Button>
      ))}
    </Stack>
  );
}
